import { useMemo } from "react";
import { ServiceQuotesOfferingForm } from "@/components/proposal-formats/ServiceQuotesOfferingForm";
import { computeOfferingReadiness, readinessSummaryLine } from "./serviceSetupReadiness";
import { ServiceSetupReadinessBadge } from "./ServiceSetupReadinessBadge";
import { ServiceJobTypesAdvancedSection } from "./ServiceJobTypesAdvancedSection";
import { ghostBtnClass, primaryBtnClass } from "./serviceShared";
import type { useServiceOfferingsCatalog } from "./useServiceOfferingsCatalog";
import type { useServiceDefinitionsPanel } from "./useServiceDefinitionsPanel";
import { useServiceOfferingDocSummaries } from "./useServiceOfferingDocSummaries";

export function ServiceSetupCatalogView(props: {
  catalog: ReturnType<typeof useServiceOfferingsCatalog>;
  definitions: ReturnType<typeof useServiceDefinitionsPanel>;
  navTick: number;
  onOpenOffering: (offeringId: string) => void;
  onOpenSharedFleet: () => void;
  onCreateService: () => void;
  onRefresh?: () => void;
}) {
  const { catalog, definitions, navTick, onOpenOffering, onOpenSharedFleet, onCreateService, onRefresh } =
    props;

  const activeOfferings = useMemo(
    () => catalog.offerings.filter((o) => o.is_active),
    [catalog.offerings]
  );
  const offeringIds = useMemo(() => activeOfferings.map((o) => o.id), [activeOfferings]);
  const docSummaries = useServiceOfferingDocSummaries(offeringIds, activeOfferings.length > 0, navTick);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" className={ghostBtnClass} onClick={onOpenSharedFleet}>
          Shared fleet
        </button>
        <button type="button" className={primaryBtnClass} onClick={onCreateService}>
          + Add service
        </button>
      </div>

      <div className="space-y-4 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] lg:items-start lg:gap-6 lg:space-y-0">
        <div className="space-y-4 min-w-0">
          {catalog.localError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
              {catalog.localError}
            </div>
          ) : null}

          {activeOfferings.length > 0 ? (
            <ul className="grid gap-3 sm:grid-cols-2">
              {activeOfferings.map((off) => {
                const docSummary = docSummaries[off.id];
                const readiness = computeOfferingReadiness({
                  offering: off,
                  rigOfferingIds: definitions.rigOfferingIds,
                  techOfferingIds: definitions.techOfferingIds,
                  docSummary,
                });
                return (
                  <li key={off.id}>
                    <button
                      type="button"
                      className="flex h-full w-full flex-col rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-indigo-300 hover:shadow-md dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-indigo-700"
                      onClick={() => onOpenOffering(off.id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{off.label}</h4>
                        <ServiceSetupReadinessBadge readiness={readiness} />
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">{off.slug}</p>
                      <p className="mt-3 text-sm text-gray-600 dark:text-zinc-400">
                        {readinessSummaryLine(readiness, docSummary?.docCount ?? 0)}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {catalog.inheritedOfferings.length > 0 ? (
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              {catalog.inheritedOfferings.length} inherited manufacturer{" "}
              {catalog.inheritedOfferings.length === 1 ? "service" : "services"} from your publisher org.
            </p>
          ) : null}

          <ServiceJobTypesAdvancedSection navTick={navTick} onChanged={onRefresh} />
        </div>

        {catalog.showForm ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-zinc-700 dark:bg-zinc-900/40 lg:sticky lg:top-4">
            <h4 className="mb-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
              {catalog.draft.id ? "Edit service" : "New service"}
            </h4>
            <ServiceQuotesOfferingForm
              draft={catalog.draft}
              setDraft={catalog.setDraft}
              queues={catalog.queues}
              queuesUnavailable={catalog.queuesUnavailable}
              actionBusy={catalog.actionBusy}
              slugReadOnly={!!catalog.draft.id}
              hideQuotable
              compact
              onSave={async () => {
                const saved = await catalog.saveOffering();
                if (saved) onOpenOffering(saved.id);
              }}
              onCancel={catalog.cancelForm}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
